import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Utility } from "@/utility/Utility";
import { safeSessionRemoveItem, storage } from "@/utils/safeStorage";
import { onboardingProgressService } from "@/services/OnboardingProgressService";

type AuthStore = {
  user: object;
  setUser: (user: object) => void;
  token: string;
  role: string;
  organizationId: string;
  subscription?: { status: string; packageId?: string; plan?: string };
  appInfo?: {
    appId: string;
    clientId: string;
    appName: string;
    createdAt: string;
  };
  metadata?: {
    authTimestamp: number;
    clientIp: string;
    userAgent: string;
    country: string;
    timezone: string;
  };
  initCompleted: boolean;
  setToken: (token: string) => void;
  logoutUser: () => Promise<void>;
  isLoggedIn: () => boolean;
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: {},
      token: "",
      role: "",
      organizationId: "",
      initCompleted: false,
      setUser: (user: object) => set({ user }),
      setToken: (token: string) => {
        if (token) {
          try {
            const decodedToken = Utility.decodeToken(token);

            // Defensive access to user properties
            const user = decodedToken?.user || {};
            const role = user?.role || "";
            const organizationId = user?.organizationId || "";

            set({
              token,
              role,
              organizationId,
              subscription: decodedToken?.subscription,
              appInfo: decodedToken?.appInfo,
              metadata: decodedToken?.metadata,
              initCompleted: true
            });
          } catch (error) {
            console.error("Failed to decode token:", error);
            set({ token: "", initCompleted: true });
          }
        } else {
          set({ token: "", initCompleted: true });
        }
      },
      logoutUser: async () => {
        // Save onboarding progress before logout
        try {
          safeSessionRemoveItem("accessToken");
          await onboardingProgressService.saveProgressOnLogout();
        } catch (error) {
          console.error("Failed to save onboarding progress on logout:", error);
        }

        safeSessionRemoveItem("accessToken");
        set({
          user: {},
          token: "",
          role: "",
          organizationId: "",
          subscription: undefined,
          appInfo: undefined,
          metadata: undefined,
          initCompleted: true
        });
      },
      isLoggedIn: () => {
        const state = get();
        return !!state.token;
      }
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => ({
        getItem: async (key: string) => {
          try {
            return await storage.getItem(key);
          } catch {
            return null;
          }
        },
        setItem: async (key: string, value: string) => {
          try {
            await storage.setItem(key, value);
          } catch {
            // Silent fail
          }
        },
        removeItem: async (key: string) => {
          try {
            await storage.removeItem(key);
          } catch {
            // Silent fail
          }
        }
      }))
    }
  )
);
